// Agent template catalog — pre-built agents a user can add to their workspace
// and then customize. Using one creates the agent from its name and instructions.

import type { Template, TemplateCategory } from "@/shared/types";

export const TEMPLATE_CATEGORIES: ("All" | TemplateCategory)[] = [
  "All",
  "Networking",
  "Sales",
  "Recruiting",
  "Marketing",
  "Productivity",
];

export const TEMPLATES_CATALOG: Template[] = [
  {
    id: "linkedin-agent",
    name: "LinkedIn Agent",
    tagline:
      "A specialized LinkedIn operator that handles outreach, conversations, and your entire job hunt — end to end.",
    category: "Networking",
    hue: 212,
    seed: "linkedin-agent",
    official: true,
    version: "v3",
    model: "definable-max",
    apps: [
      { id: "linkedin", requirement: "Required" },
      { id: "gmail", requirement: "Optional — email fallbacks" },
      { id: "drive", requirement: "Optional — resume & docs" },
    ],
    skills: ["web-research", "brand-voice"],
    instructions:
      "You are a LinkedIn specialist acting on behalf of the user. Write in their voice: warm, specific, never salesy. Personalize every outreach message using the recipient's profile, recent posts, and mutual context — never send a generic template. Keep connection notes under 280 characters. For job applications, tailor each note to the role and pull the latest resume from Drive. Always show drafts for approval before sending anything on the user's behalf. Track every touchpoint so follow-ups reference the previous message. Never message the same person twice in one week.",
    capabilities: [
      {
        id: "outreach",
        icon: "send",
        name: "Outreach",
        description: "Personalized connection requests and cold messages at scale.",
        prompt:
          "Reach out to 20 product managers at Series B fintech startups with a personalized note about our beta.",
      },
      {
        id: "followups",
        icon: "repeat",
        name: "Follow-ups",
        description: "Timely, context-aware nudges that reference the last touchpoint.",
        prompt:
          "Follow up with everyone who accepted my connection request this week but hasn't replied yet.",
      },
      {
        id: "send",
        icon: "message",
        name: "Send messages",
        description: "Drafts and sends messages in your voice, with approval first.",
        prompt:
          "Message my recruiter contacts and let them know I'm open to senior frontend roles.",
      },
      {
        id: "receive",
        icon: "inbox",
        name: "Receive & triage",
        description: "Reads incoming messages, summarizes, and flags what needs you.",
        prompt:
          "Summarize my unread LinkedIn messages and flag anything that needs a reply today.",
      },
      {
        id: "apply",
        icon: "briefcase",
        name: "Apply for jobs",
        description: "Tailors your resume note and applies to matching openings.",
        prompt:
          "Apply to remote senior React roles posted this week — use my resume from Drive and tailor each note.",
      },
      {
        id: "find",
        icon: "telescope",
        name: "Find jobs",
        description: "Scans openings by role, location, company size, and stage.",
        prompt:
          "Find 15 open Product Designer roles in Berlin at companies with 50–500 employees.",
      },
    ],
    tasks: [
      { id: "t1", name: "Morning inbox triage", detail: "Summarize new messages, flag replies needed", schedule: "Daily 8am" },
      { id: "t2", name: "Pipeline follow-ups", detail: "Nudge silent threads older than 4 days", schedule: "Mon 9am" },
    ],
    knowledge: [
      { id: "k1", name: "Your resume & portfolio", hint: "Drop in a PDF or connect Drive" },
      { id: "k2", name: "Target companies list", hint: "Optional CSV of companies to prioritize" },
    ],
  },

  {
    id: "sales-prospector",
    name: "Sales Prospector",
    tagline: "Builds target lists, enriches leads, and books first meetings on autopilot.",
    category: "Sales",
    hue: 150,
    seed: "sales-prospector",
    official: true,
    skills: ["web-research", "email-triage"],
    version: "v5",
    model: "definable-pro",
    apps: [
      { id: "gmail", requirement: "Required" },
      { id: "gcalendar", requirement: "Required — booking" },
      { id: "airtable", requirement: "Optional — CRM sync" },
    ],
    instructions:
      "You are an SDR working the top of the funnel. Research each prospect before writing a word. Sequences are 3 touches max, each shorter than the last. Book meetings directly on the calendar when a prospect shows intent.",
    capabilities: [
      { id: "lists", icon: "telescope", name: "Build lists", description: "Finds accounts matching your ICP.", prompt: "Build a list of 50 heads of ops at logistics companies in the EU." },
      { id: "enrich", icon: "inbox", name: "Enrich leads", description: "Fills in role, size, tech stack, recent news.", prompt: "Enrich this CSV of leads with company size and latest funding." },
      { id: "sequence", icon: "send", name: "Run sequences", description: "3-touch email sequences in your voice.", prompt: "Start a sequence for the Q3 logistics list, 3 touches over 10 days." },
      { id: "book", icon: "repeat", name: "Book meetings", description: "Handles replies and books straight to calendar.", prompt: "Reply to interested leads and offer slots from my calendar next week." },
    ],
    tasks: [{ id: "t1", name: "Reply sweep", detail: "Classify replies, book or close out", schedule: "Daily 9am" }],
    knowledge: [{ id: "k1", name: "ICP & positioning doc", hint: "Who you sell to and why you win" }],
    // Ships reachable out of the box — email address + Slack for the sales channel.
    interfaces: [{ type: "email" }, { type: "slack" }],
  },

  {
    id: "recruiter-agent",
    name: "Recruiter Agent",
    tagline: "Sources candidates, screens profiles, and keeps every pipeline stage moving.",
    category: "Recruiting",
    hue: 268,
    seed: "recruiter-agent",
    official: true,
    skills: ["web-research", "meeting-notes"],
    version: "v2",
    model: "definable-max",
    apps: [
      { id: "linkedin", requirement: "Required" },
      { id: "gcalendar", requirement: "Optional — interviews" },
      { id: "notion", requirement: "Optional — pipeline board" },
    ],
    instructions:
      "You are a technical recruiter. Source candidates that genuinely match the brief — skills over keywords. First messages are short, honest about the role, and mention one specific thing from the candidate's profile.",
    capabilities: [
      { id: "source", icon: "telescope", name: "Source candidates", description: "Finds profiles matching a role brief.", prompt: "Source 30 senior backend engineers with Go experience open to relocation." },
      { id: "screen", icon: "inbox", name: "Screen profiles", description: "Scores candidates against the brief.", prompt: "Screen this week's applicants for the platform engineer role and rank the top 10." },
      { id: "reach", icon: "send", name: "Candidate outreach", description: "Personal first messages, never spray-and-pray.", prompt: "Message the top 10 ranked candidates about the platform engineer role." },
      { id: "schedule", icon: "repeat", name: "Coordinate interviews", description: "Finds slots and keeps loops on schedule.", prompt: "Schedule first-round calls with everyone who replied positively this week." },
    ],
    tasks: [{ id: "t1", name: "Pipeline digest", detail: "Stage counts and stuck candidates", schedule: "Fri 4pm" }],
    knowledge: [{ id: "k1", name: "Role briefs", hint: "One doc per open role" }],
  },

  {
    id: "content-marketer",
    name: "Content Marketer",
    tagline: "Turns one idea into a week of posts, matched to your voice and channels.",
    category: "Marketing",
    hue: 20,
    seed: "content-marketer",
    official: true,
    version: "v7",
    model: "definable-pro",
    apps: [
      { id: "notion", requirement: "Optional — content calendar" },
      { id: "drive", requirement: "Optional — brand assets" },
    ],
    instructions:
      "You are a content strategist and writer. Every piece starts from the audience's problem, not the product. Repurpose long-form into channel-native formats — a LinkedIn post is not a tweet thread.",
    capabilities: [
      { id: "ideate", icon: "telescope", name: "Ideate", description: "Angles and hooks from one seed idea.", prompt: "Give me 10 post angles from our latest changelog." },
      { id: "draft", icon: "send", name: "Draft posts", description: "Channel-native drafts in your voice.", prompt: "Draft a week of LinkedIn posts from this blog article." },
      { id: "calendar", icon: "repeat", name: "Plan calendar", description: "Slots content across the week.", prompt: "Plan next week's content calendar around the product launch." },
    ],
    tasks: [{ id: "t1", name: "Trend scan", detail: "What's moving in your niche", schedule: "Daily 8am" }],
    knowledge: [{ id: "k1", name: "Voice & style guide", hint: "Samples of writing you like" }],
  },

  {
    id: "meeting-prep",
    name: "Meeting Prep Agent",
    tagline: "Briefs you before every meeting — who's in the room, context, and talking points.",
    category: "Productivity",
    hue: 45,
    seed: "meeting-prep",
    official: true,
    version: "v4",
    model: "definable-pro",
    apps: [
      { id: "gcalendar", requirement: "Required" },
      { id: "gmail", requirement: "Optional — thread context" },
      { id: "notion", requirement: "Optional — meeting notes" },
    ],
    instructions:
      "You prepare the user for meetings. For each event: who's attending, last interactions, open threads, and three suggested talking points. Briefs are one page max, delivered 30 minutes before.",
    capabilities: [
      { id: "brief", icon: "inbox", name: "Pre-meeting briefs", description: "Context on people and open items.", prompt: "Brief me on tomorrow's 10am with the Acme team." },
      { id: "notes", icon: "send", name: "Follow-up notes", description: "Action items and recap after the call.", prompt: "Draft a recap of today's design review with action items." },
      { id: "watch", icon: "repeat", name: "Calendar watch", description: "Flags conflicts and prep-heavy days.", prompt: "What does my Thursday look like — anything I should prepare for?" },
    ],
    tasks: [{ id: "t1", name: "Daily brief", detail: "All meetings for the day, prepped", schedule: "Daily 7:30am" }],
    knowledge: [],
    // Briefs land where you already look — WhatsApp on your phone.
    interfaces: [{ type: "whatsapp" }],
  },
];
